// =============================================================================
// AFFILIATE REF — the one place that reads `localStorage.affiliate_ref`.
//
// Flat next to App.js and tracking.js on purpose. There is no src/utils/ and no
// src/services/ in this app; flat modules beside App.js (tracking.js,
// usLocations.js) are the house shape.
//
// 🔴 DELIBERATELY NOT IN tracking.js. That module's own header states it does not
//    touch `affiliate_ref`, and keeping that true matters: the two are different
//    attribution systems that happen to both involve "where did this visitor come
//    from". Link tracking is 30 days, LAST TOUCH, and feeds a dashboard. This is
//    365 days, FIRST TOUCH, and feeds a revenue share paid in real money. Merging
//    them into one file is how someone later "unifies the windows" and silently
//    moves four founding affiliates onto a 30-day cookie.
//
// 🔒 THE KEY AND THE SHAPE ARE FROZEN. `affiliate_ref` = {ref, capturedAt}, written
//    by AffiliateLanding.storeAffiliateRef() and by nothing else. Four live
//    revenue-share relationships hang off exactly that key, that shape, and the
//    `ref` field the three signup funnels attach to their POST. This module adds an
//    age check on read; it changes neither the key, the shape, nor the writer.
//
// 🔒 EVERY STORAGE ACCESS IS IN ITS OWN try/catch AND WORKS WHEN IT THROWS. Safari
//    private browsing and "block all cookies" throw on the `window.localStorage`
//    property access itself, not just on getItem — so the whole expression sits
//    inside the try. Failing to read a ref must never break a signup.
//
// Consumers: SignUp.js (live funnel), SignUpInfo.js and CommunitySignUp.js
// (prelaunch funnels). All three previously inlined their own copy of the read.
// =============================================================================

const KEY_AFFILIATE_REF = 'affiliate_ref';

// 365 days, FIRST TOUCH. Owner's decision 2026-09-10: "the attachment should only
// last 1 year of those users who clicked but never signed up." Before this, a click
// from any date in history still attributed a signup today, forever.
//
// ⚠️ NOT the same 1 year as the server's `attribution_expires_at`. That one governs
//    an existing free signup converting to paid. This one governs a click that
//    never became a signup at all. They are independent windows that happen to
//    share a length.
export const AFFILIATE_REF_TTL_MS = 365 * 24 * 60 * 60 * 1000;

// A capturedAt in the FUTURE means the clock moved, not that the click is fresh
// forever. A little slack keeps a phone that is a few minutes (or a timezone
// mistake) ahead from dropping a real referral; anything beyond that is treated as
// unusable and therefore expired, which also disposes of Infinity.
const FUTURE_SKEW_GRACE_MS = 24 * 60 * 60 * 1000;

function readRaw() {
    try { return window.localStorage.getItem(KEY_AFFILIATE_REF); } catch (_) { return null; /* storage disabled */ }
}

/**
 * Remove the stored ref. Total — never throws.
 */
export function clearAffiliateRef() {
    try { window.localStorage.removeItem(KEY_AFFILIATE_REF); } catch (_) { /* storage disabled */ }
}

/**
 * The affiliate slug this visitor should be attributed to, or null.
 *
 * Null is a real answer, not a failure: no click, a click older than a year, or a
 * stored value this module cannot vouch for. EXPIRED AND MALFORMED BOTH CLEAR THE
 * KEY, so a dead ref stops being re-examined on every subsequent signup attempt.
 *
 * 🔴 FAILS TOWARD NOT ATTRIBUTING. A missing or non-numeric `capturedAt` — a value
 *    written before that field existed, or hand-edited — is treated as EXPIRED, not
 *    as valid-forever. Wrongly crediting an affiliate for a signup they did not
 *    cause is money out the door and it is invisible; missing one is neither.
 *
 * Never throws. Callers can use the result directly.
 */
export function readAffiliateRef() {
    const raw = readRaw();
    if (!raw) return null;

    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (_) {
        clearAffiliateRef(); // not JSON at all
        return null;
    }

    if (!parsed || typeof parsed.ref !== 'string' || !parsed.ref) {
        clearAffiliateRef();
        return null;
    }

    // typeof-number is the same gate tracking.js applies to its own stored `at`.
    // NaN fails `> 0`; Infinity fails the skew bound below. A numeric STRING is
    // rejected on purpose — the only writer of this key writes a real number, so
    // anything else is a value we cannot vouch for.
    const capturedAt = parsed.capturedAt;
    if (typeof capturedAt !== 'number' || !(capturedAt > 0)) {
        clearAffiliateRef();
        return null;
    }

    const age = Date.now() - capturedAt;
    if (age >= AFFILIATE_REF_TTL_MS || age <= -FUTURE_SKEW_GRACE_MS) {
        clearAffiliateRef();
        return null;
    }

    return parsed.ref;
}
